import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon,
  ArrowBack as BackIcon,
  CheckCircle,
  Info
} from '@mui/icons-material';
import Papa from 'papaparse';
import api from '../axiosConfig';

const ServiceImport = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  const templateData = [
    {
      service_name: 'Ladies Cut & Blow Dry',
      description: 'Cut and blow dry service for ladies',
      duration: '60',
      price: '45.00',
      category: 'Hair Services',
      product_usage: 'Shampoo:20ml,Conditioner:15ml'
    },
    {
      service_name: 'Gents Cut',
      description: 'Standard gents haircut',
      duration: '30',
      price: '25.00',
      category: 'Hair Services',
      product_usage: ''
    },
    {
      service_name: 'Full Head Highlights',
      description: 'Full head highlights with toner',
      duration: '120',
      price: '95.00',
      category: 'Color Services',
      product_usage: 'Bleach:100g,Toner:50ml,Developer:100ml'
    }
  ];

  const handleDownloadTemplate = () => {
    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'service_import_template.csv';
    link.click();
  };

  const validateRow = (row, rowIndex) => {
    const errors = [];
    
    // Required fields
    if (!row.service_name || row.service_name.trim() === '') {
      errors.push(`Row ${rowIndex + 2}: Service name is required`);
    }
    
    if (!row.duration || isNaN(parseInt(row.duration))) {
      errors.push(`Row ${rowIndex + 2}: Valid duration (in minutes) is required`);
    } else {
      const duration = parseInt(row.duration);
      if (duration < 5 || duration > 480) {
        errors.push(`Row ${rowIndex + 2}: Duration must be between 5 and 480 minutes`);
      }
    }
    
    if (!row.price || isNaN(parseFloat(row.price))) {
      errors.push(`Row ${rowIndex + 2}: Valid price is required`);
    } else {
      const price = parseFloat(row.price);
      if (price < 0) {
        errors.push(`Row ${rowIndex + 2}: Price cannot be negative`);
      }
    }

    // Validate product usage format if provided
    if (row.product_usage && row.product_usage.trim() !== '') {
      const products = row.product_usage.split(',');
      products.forEach((product, idx) => {
        const parts = product.split(':');
        if (parts.length !== 2) {
          errors.push(`Row ${rowIndex + 2}: Product usage format should be "ProductName:Quantity" (e.g., "Shampoo:20ml")`);
        }
      });
    }

    return errors;
  };

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setIsProcessing(true);
    setValidationErrors([]);
    setImportResults(null);
    setMessage('');

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: 'greedy',
      transform: (value) => value.trim(),
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: async (results) => {
        try {
          if (results.errors.length > 0) {
            setMessageType('error');
            setMessage('Error parsing CSV file. Please check the format.');
            setIsProcessing(false);
            return;
          }

          if (!results.data || results.data.length === 0) {
            setMessageType('error');
            setMessage('No data found in CSV file');
            setIsProcessing(false);
            return;
          }

          // Validate all rows
          const allErrors = [];
          results.data.forEach((row, index) => {
            const rowErrors = validateRow(row, index);
            allErrors.push(...rowErrors);
          });

          if (allErrors.length > 0) {
            setValidationErrors(allErrors);
            setMessageType('error');
            setMessage(`Found ${allErrors.length} validation error(s). Please fix and try again.`);
            setIsProcessing(false);
            return;
          }

          // Process and upload
          const formData = new FormData();
          formData.append('file', selectedFile);

          const response = await api.post('/csv/import/services', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          setImportResults(response.data);
          setMessageType(response.data.success > 0 ? 'success' : 'error');
          setMessage(
            response.data.success > 0
              ? `Successfully imported ${response.data.success} service(s)`
              : 'Import failed. Please check errors below.'
          );

        } catch (error) {
          console.error('Upload error:', error);
          setMessageType('error');
          setMessage(
            error.response?.data?.error || 
            'Error uploading file. Please try again.'
          );
        } finally {
          setIsProcessing(false);
          event.target.value = '';
        }
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        setMessageType('error');
        setMessage('Error reading CSV file. Please check the format.');
        setIsProcessing(false);
        event.target.value = '';
      }
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
        
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/import')}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Import Services
          </Typography>
        </Box>

        <Paper>
          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} variant="fullWidth">
            <Tab label="Instructions" />
            <Tab label="Import" />
          </Tabs>

          {/* Instructions Tab */}
          {currentTab === 0 && (
            <Box sx={{ p: 4 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Before You Start
                </Typography>
                <Typography variant="body2">
                  Download the template, fill in your service menu, and upload the completed CSV file.
                </Typography>
              </Alert>

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Required Fields
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="service_name" 
                    secondary="Name of the service (e.g., Ladies Cut & Blow Dry)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="duration" 
                    secondary="Duration in minutes (e.g., 60)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="price" 
                    secondary="Service price (e.g., 45.00)"
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Optional Fields
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText 
                    primary="description" 
                    secondary="Service description"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText 
                    primary="category" 
                    secondary="Service category (will be created if doesn't exist)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Info color="action" /></ListItemIcon>
                  <ListItemText 
                    primary="product_usage" 
                    secondary='Products used in service. Format: "ProductName:Quantity,ProductName:Quantity" (e.g., "Shampoo:20ml,Toner:50ml")'
                  />
                </ListItem>
              </List>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Prices should be entered without currency symbols (e.g., 45.00 not £45.00). 
                  Duration should be in minutes. Product usage is optional but must follow the correct format if provided.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Import Tab */}
          {currentTab === 1 && (
            <Box sx={{ p: 4 }}>
              {message && (
                <Alert severity={messageType} sx={{ mb: 3 }}>
                  {message}
                </Alert>
              )}

              {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Validation Errors:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                    {validationErrors.slice(0, 10).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                    {validationErrors.length > 10 && (
                      <li>...and {validationErrors.length - 10} more errors</li>
                    )}
                  </Box>
                </Alert>
              )}

              {importResults && (
                <Alert 
                  severity={
                    importResults.success > 0 && importResults.failures.length === 0
                      ? 'success'
                      : importResults.success > 0
                      ? 'warning'
                      : 'error'
                  }
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Import Results:
                  </Typography>
                  <ul>
                    <li>Successfully imported: {importResults.success}</li>
                    {importResults.failures && importResults.failures.length > 0 && (
                      <li>Failed imports: {importResults.failures.length}</li>
                    )}
                    {importResults.duplicates && importResults.duplicates > 0 && (
                      <li>Duplicates skipped: {importResults.duplicates}</li>
                    )}
                  </ul>
                  {importResults.failures && importResults.failures.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" fontWeight="bold">
                        Errors:
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                        {importResults.failures.slice(0, 5).map((failure, index) => (
                          <li key={index}>{failure}</li>
                        ))}
                        {importResults.failures.length > 5 && (
                          <li>...and {importResults.failures.length - 5} more errors</li>
                        )}
                      </Box>
                    </Box>
                  )}
                </Alert>
              )}

              <Button
                startIcon={<DownloadIcon />}
                variant="outlined"
                onClick={handleDownloadTemplate}
                sx={{ mb: 3 }}
                disabled={isProcessing}
              >
                Download Template
              </Button>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Upload CSV File
              </Typography>

              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="service-csv-input"
                disabled={isProcessing}
              />
              <label htmlFor="service-csv-input">
                <Button
                  startIcon={isProcessing ? <CircularProgress size={20} /> : <UploadIcon />}
                  variant="contained"
                  component="span"
                  disabled={isProcessing}
                  size="large"
                >
                  {isProcessing ? 'Processing...' : 'Choose CSV File'}
                </Button>
              </label>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ServiceImport;